import { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Building2, ScanSearch } from "lucide-react";
import { ResourceDisclosureHeader } from './ResourceDisclosure';
import { userAPI } from "../UserAPI";
import { Asset, Building, Floor } from '@the-real-insight/tri-model';
import { FloorplanEditor } from "../floorplanEditor/FloorplanEditor";
import { useAtom } from "jotai";
import { authenticationAtom } from "../State";

import { Button } from "@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

// Global state for expanded resources to persist across re-renders
const expandedResources = new Map();

/**
 * @param {{resource: any, stableKey?: string}} props
 */
export const FloorplanResource = ({ resource, stableKey }) => {
    // Always call hooks first
    const [authentication] = useAtom(authenticationAtom);
    const navigate = useNavigate();

    const resourceKey = stableKey || resource?.reference || resource?._id;
    const [expanded, setExpanded] = useState(() => expandedResources.get(resourceKey) || false);

    const [building, setBuilding] = useState(null);
    const [floor, setFloor] = useState(null);
    const [assets, setAssets] = useState([]);
    const [title, setTitle] = useState(null);

    useEffect(() => {
        const call = async () => {
            let newResource = null;

            // Load resource from reference

            if (resource.reference) {
                newResource = await userAPI.getResource(authentication.customer, resource.reference);
            } else {
                newResource = resource;
            }

            console.log(newResource);

            setTitle(newResource.title || newResource.details);

            if (newResource.data.building) {
                setBuilding(new Building(newResource.data.building));

                if (newResource.data.building.floors && newResource.data.building.floors.length > 0) {
                    setFloor(new Floor(newResource.data.building.floors[0]));

                    if (newResource.data.assets) {
                        setAssets(newResource.data.assets.map(asset => new Asset(asset)));
                    }
                }
            }
        }

        // Only call if we have a reference or if we don't have local data yet
        if (resource.reference || !title) {
            call();
        }
    }, [resource.reference, resource._id, authentication.customer]);

    // Apply color scheme

    return <div className="box-border w-full">
        {/* Der Grundriss-Editor baut eine Zeichenfläche nach Containergröße auf
            und bliebe zusammengeklappt auf 0 Pixel Höhe. Deshalb hier weiter
            bedingtes Rendern statt der Grid-Animation — nur Kopfzeile und
            Chevron folgen dem gemeinsamen Muster. */}
        <ResourceDisclosureHeader
            expanded={expanded}
            onToggle={(e) => {
                if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                }
                const newExpanded = !expanded;
                setExpanded(newExpanded);
                // Store in global state to persist across re-renders
                expandedResources.set(resourceKey, newExpanded);
            }}
            testId="chat-resource-floorplan-toggle"
            icon={<ScanSearch aria-hidden="true" className="h-5 w-5 shrink-0" />}
            title={title}
            actions={
            <TooltipProvider delayDuration={300}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                            aria-label="Zum Gebäude"
                            data-testid="chat-resource-floorplan-to-building"
                            onClick={() => navigate(`/entity?selectedEntityType=Building&selectedEntity=${building?._id}&tab=location`)}>
                            <Building2 aria-hidden="true" className="h-4 w-4" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>Zum Gebäude</TooltipContent>
                </Tooltip>
            </TooltipProvider>
            }
        />
        {expanded
            ?
            <div className="p-4">
                <div className="flex justify-center">
                    {floor ?
                        <FloorplanEditor floor={floor} readonly assets={assets} />
                        :
                        <div>No floorplan available</div>
                    }
                </div>
            </div>
            :
            <></>
        }
    </div>;
}
